import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { Entity } from '@backstage/catalog-model';
import {
  CatalogAutocomplete,
  EntityFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import {
  FilterMap,
  countAvailableValues,
  sameValues,
  toStringArray,
  withSelected,
} from './availableValues';

type ValuesFilter = EntityFilter & { values: string[] };

export type FacetedAutocompletePickerProps = {
  label: string;
  name: string;
  getValues: (entity: Entity) => string[];
  Filter: new (values: string[]) => ValuesFilter;
  getOptionLabel?: (option: string) => string;
  showCounts?: boolean;
  initialSelectedOptions?: string[];
  fallback: ReactNode;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const identity = (option: string) => option;

const FacetedAutocompletePickerContent = ({
  label,
  name,
  getValues,
  Filter,
  getOptionLabel = identity,
  showCounts,
  initialSelectedOptions = [],
}: Omit<FacetedAutocompletePickerProps, 'fallback'>) => {
  const { filters, queryParameters, updateFilters, backendEntities } =
    useEntityList();
  const filterMap = filters as unknown as FilterMap;
  const queryParameter = (
    queryParameters as Record<string, string | string[] | undefined>
  )[name];
  const queryValues = useMemo(
    () => toStringArray(queryParameter),
    [queryParameter],
  );

  const [selected, setSelected] = useState<string[]>(() => {
    const current = filterMap[name] as ValuesFilter | undefined;
    const initial = queryValues.length
      ? queryValues
      : current?.values ?? initialSelectedOptions;
    return new Filter(initial).values;
  });

  useEffect(() => {
    if (!queryValues.length) {
      return;
    }
    const next = new Filter(queryValues).values;
    setSelected(previous => (sameValues(previous, next) ? previous : next));
  }, [queryValues, Filter]);

  useEffect(() => {
    updateFilters({
      [name]: selected.length ? new Filter(selected) : undefined,
    } as Parameters<typeof updateFilters>[0]);
  }, [name, selected, Filter, updateFilters]);

  const counts = useMemo(
    () => countAvailableValues(backendEntities, filterMap, name, getValues),
    [backendEntities, filterMap, name, getValues],
  );
  const options = useMemo(
    () => withSelected(counts, selected),
    [counts, selected],
  );

  if (!options.length) {
    return null;
  }

  return (
    <Box pb={1} pt={1}>
      <CatalogAutocomplete<string, true>
        multiple
        disableCloseOnSelect
        label={label}
        name={`${name}-picker`}
        options={options}
        value={selected}
        getOptionLabel={getOptionLabel}
        onChange={(_event, value) => setSelected(value)}
        renderOption={(option, { selected: isSelected }) => (
          <FormControlLabel
            control={
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                checked={isSelected}
              />
            }
            label={
              showCounts
                ? `${getOptionLabel(option)} (${counts[option] ?? 0})`
                : getOptionLabel(option)
            }
            onClick={event => event.preventDefault()}
          />
        )}
      />
    </Box>
  );
};

export const FacetedAutocompletePicker = ({
  fallback,
  ...props
}: FacetedAutocompletePickerProps) => {
  const { paginationMode } = useEntityList();
  if (paginationMode !== 'none') {
    return <>{fallback}</>;
  }
  return <FacetedAutocompletePickerContent {...props} />;
};
